/**
 * Bypass del onboarding para probar la app en local — ADR-2026-07-30-bypass-dev-onboarding-entrar-app.
 *
 * ⚠️ SÓLO DEV. Todo está guardado por `import.meta.env.DEV`: en el build de
 * producción `saltearOnboarding()` devuelve siempre `false` y los `if` que la
 * consultan se podan, así que los guards quedan exactamente como estaban.
 *
 * Para qué: hoy hay dos redirects que sacan al usuario de la app hasta que
 * termina el alta, y ambos molestan cuando lo único que querés es mirar una
 * pantalla interna:
 *
 *  1. `ProtectedRoute` → `/configuracion-cuenta` si el perfil no tiene
 *     `id_company` (cuenta recién creada).
 *  2. `DashboardLayout` → `/onboarding/revisar-marca/:id` si quedó una marca en
 *     `is_draft = true` (alta abandonada a la mitad) — este es el que deja la
 *     cuenta "pegada" aunque ya tengas empresa.
 *
 * Con el flag prendido los dos se saltean. No escribe nada en Supabase: no crea
 * la empresa ni cierra el draft, sólo deja de rebotarte.
 *
 * Cómo se prende (mismo efecto, el que te quede más a mano):
 *   - URL:      http://localhost:5173/?skip-onboarding=1   (queda persistido)
 *   - apagar:   http://localhost:5173/?skip-onboarding=0
 *   - consola:  atlasSkipOnboarding(true) / atlasSkipOnboarding(false)
 */

const KEY = 'atlas-dev-skip-onboarding';
const PARAM = 'skip-onboarding';

/** `true` sólo en `npm run dev`. En el build es `false` y todo esto se poda. */
export const DEV_SKIP_ONBOARDING_ENABLED = import.meta.env.DEV;

function leerFlag(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

function escribirFlag(valor: boolean): void {
  try {
    if (valor) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/**
 * Prende/apaga el bypass. Exportada para el helper de consola y para cualquier
 * panel dev que quiera un toggle.
 */
export function setSaltearOnboarding(valor: boolean): void {
  if (!DEV_SKIP_ONBOARDING_ENABLED) return;
  escribirFlag(valor);
}

/**
 * ¿Hay que saltear los guards de onboarding?
 *
 * Se lee de forma síncrona en los guards (no es un hook) porque corren dentro de
 * efectos y callbacks async, antes de cualquier render.
 */
export function saltearOnboarding(): boolean {
  if (!DEV_SKIP_ONBOARDING_ENABLED) return false;
  return leerFlag();
}

/**
 * Aplica `?skip-onboarding=1|0` si viene en la URL y limpia el parámetro para
 * que no se arrastre en los links. Se llama una vez al bootear la app.
 *
 * El param se procesa acá y no en un efecto de ruta porque el redirect a
 * `/configuracion-cuenta` es un `window.location.href`: si el flag no está
 * escrito antes del primer chequeo de perfil, la navegación se lo lleva puesto.
 */
export function aplicarSkipOnboardingDeLaURL(): void {
  if (!DEV_SKIP_ONBOARDING_ENABLED) return;
  try {
    const url = new URL(window.location.href);
    const valor = url.searchParams.get(PARAM);
    if (valor === null) return;
    escribirFlag(valor !== '0' && valor !== 'false');
    url.searchParams.delete(PARAM);
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* noop */
  }
}

/** Helper de consola: `atlasSkipOnboarding(true)`. */
export function registrarHelperDeConsola(): void {
  if (!DEV_SKIP_ONBOARDING_ENABLED) return;
  (window as any).atlasSkipOnboarding = (valor = true) => {
    escribirFlag(Boolean(valor));
    console.log(
      `[dev] Bypass de onboarding ${valor ? 'ACTIVADO' : 'desactivado'}. Recargá la página.`,
    );
    return Boolean(valor);
  };
}
